// src/components/Sidebar.js
import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Typography, Avatar, Divider } from 'antd';
import {
  FileTextOutlined,
  MobileOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  AppstoreOutlined,
  TeamOutlined,
  CalendarOutlined,
  BellOutlined,
  CompassOutlined,
  CloseOutlined
} from '@ant-design/icons';

import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import agustinosLogo from '../assets/logo-agustinos.png';
import { brand } from '../styles/theme';
import useIsMobile from '../hooks/useIsMobile';

const { Sider } = Layout;
const { Text } = Typography;

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isAdmin, isCamino } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
  };

  // En móvil el sidebar funciona como drawer: el modo "colapsado a iconos" no
  // aplica y, si se quedaba activo, su botón de expandir caía justo debajo del
  // hamburguesa fijo y no había forma de volver al menú completo.
  useEffect(() => {
    if (isMobile && collapsed) setCollapsed(false);
  }, [isMobile, collapsed]);

  // Con el drawer abierto, bloquear el scroll de la página de detrás: sin esto
  // el gesto se lo llevaba el contenido del CMS en vez del propio menú.
  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobile, mobileOpen]);

  // Función para navegar y cerrar menú móvil
  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false); // Cerrar sidebar en móvil después de navegar
  };

  // Item del Camino, accesible para admin y rol "camino".
  const caminoItem = {
    key: 'camino',
    icon: <CompassOutlined />,
    label: 'Camino de Santiago',
    onClick: () => handleNavigate('/camino')
  };

  // Construir menú dinámicamente según el rol
  const buildMenuItems = () => {
    if (isCamino() && !isAdmin()) {
      return [caminoItem];
    }

    const items = [
      {
        key: 'dashboard',
        icon: <AppstoreOutlined />,
        label: 'Escritorio',
        onClick: () => handleNavigate('/dashboard')
      },
      {
        key: 'contenido',
        icon: <FileTextOutlined />,
        label: 'Contenido',
        children: [
          {
            key: 'articles',
            label: 'Lista de Contenido',
            onClick: () => handleNavigate('/articles')
          },
          {
            key: 'new-article',
            label: 'Crear nuevo',
            onClick: () => handleNavigate('/articles/new')
          },
          {
            key: 'trash',
            label: 'Papelera',
            onClick: () => handleNavigate('/trash')
          }
        ]
      },
      {
        key: 'mi-app',
        icon: <MobileOutlined />,
        label: 'Mi app',
        onClick: () => handleNavigate('/mi-app')
      },
      {
        key: 'calendar',
        icon: <CalendarOutlined />,
        label: 'Calendario',
        onClick: () => handleNavigate('/calendar')
      },
      {
        key: 'mottos',
        icon: <CalendarOutlined />,
        label: 'Lemas del mes',
        onClick: () => handleNavigate('/mottos')
      },
      {
        key: 'notifications',
        icon: <BellOutlined />,
        label: 'Notificaciones',
        onClick: () => handleNavigate('/notifications')
      },
      caminoItem,
    ];

    // Añadir menú de Usuarios solo si es admin
    if (isAdmin()) {
      items.push({
        key: 'usuarios',
        icon: <TeamOutlined />,
        label: 'Usuarios',
        onClick: () => handleNavigate('/users')
      });
    }

    return items;
  };

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/dashboard') return ['dashboard'];
    if (path === '/articles') return ['articles'];
    if (path === '/articles/new') return ['new-article'];
    if (path.startsWith('/articles/edit/')) return ['articles'];
    if (path === '/trash') return ['trash'];
    if (path === '/mi-app') return ['mi-app'];
    if (path === '/users') return ['usuarios'];
    if (path === '/calendar') return ['calendar'];
    if (path === '/mottos') return ['mottos'];
    if (path === '/notifications') return ['notifications'];
    if (path.startsWith('/camino')) return ['camino'];
    return [];
  };

  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.includes('/articles') || path === '/trash') return ['contenido'];
    return [];
  };

  return (
    <>
      {/* Botón hamburger móvil. Actúa como interruptor: si el drawer ya está
          abierto, vuelve a cerrarlo (igual que tocar fuera). */}
      <Button
        className="mobile-menu-button"
        type="primary"
        aria-expanded={mobileOpen}
        aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
        icon={mobileOpen ? <CloseOutlined /> : <MenuUnfoldOutlined />}
        onClick={() => setMobileOpen(open => !open)}
        size="large"
        style={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1001,
          display: 'none', // Oculto por defecto, visible solo en móvil
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
      />

      {/* Backdrop para cerrar en móvil */}
      {mobileOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          style={{
            display: 'none', // Visible solo en móvil cuando está abierto
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            zIndex: 99
          }}
        />
      )}

      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={280}
        className={`main-sidebar ${mobileOpen ? 'mobile-open' : ''}`}
        style={{
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 100,
          background: 'white',
          borderRight: '1px solid #f0f0f0',
          boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
          transition: 'transform 0.3s ease-in-out'
        }}
        trigger={null}
      >
      <div
        className="sidebar-scroll"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: collapsed ? '16px 8px' : '24px 16px',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}
      >
        
        {/* Header con logo y toggle */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: 16
          }}>
            {!collapsed && (
              <div style={{ textAlign: 'center', width: '100%' }}>
                <img 
                  src={agustinosLogo} 
                  alt="Agustinos" 
                  style={{ 
                    height: 50, 
                    width: 'auto',
                    maxWidth: '100%'
                  }} 
                />
              </div>
            )}
            
            <Button
              type="text"
              className="sidebar-collapse-button"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 40,
                height: 40,
                marginLeft: collapsed ? 0 : 8
              }}
            />
          </div>
          
          {/* Info del usuario */}
          {!collapsed && (
            <div 
              onClick={() => navigate('/profile')}
              style={{ 
                background: '#f8f9fa', 
                padding: 16, 
                borderRadius: 8,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
            >
              <Avatar 
                size={48} 
                icon={<UserOutlined />} 
                style={{
                  backgroundColor: brand.primary,
                  marginBottom: 8
                }}
              />
              <div>
                <Text strong style={{ display: 'block', fontSize: 16 }}>
                  {user?.username}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {user?.role} • {user?.email}
                </Text>
              </div>
            </div>
          )}
          
          {collapsed && (
            <div 
              onClick={() => navigate('/profile')}
              style={{ textAlign: 'center', cursor: 'pointer' }}
            >
              <Avatar 
                size={40} 
                icon={<UserOutlined />} 
                style={{ backgroundColor: brand.primary }}
              />
            </div>
          )}
        </div>

        {/* Menú principal - dinámico según rol */}
        <div style={{ flex: 1 }}>
          <Menu
            mode="inline"
            selectedKeys={getSelectedKeys()}
            defaultOpenKeys={getOpenKeys()}
            items={buildMenuItems()}
            style={{ 
              border: 'none',
              fontSize: '14px'
            }}
            inlineIndent={16}
          />
        </div>

        {/* Botón de cerrar sesión */}
        <div style={{ marginTop: 'auto', paddingTop: 16 }}>
          <Divider style={{ margin: '16px 0' }} />
          <Button
            danger
            block={!collapsed}
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{
              height: 40,
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            {!collapsed && 'Cerrar Sesión'}
          </Button>
        </div>
      </div>
    </Sider>

    {/* Estilos responsive */}
    <style jsx="true">{`
      @media (max-width: 768px) {
        /* Botón hamburger visible solo en móvil */
        .mobile-menu-button {
          display: block !important;
        }

        /* Sidebar oculto por defecto en móvil */
        .main-sidebar {
          transform: translateX(-100%);
          z-index: 1000 !important;
        }

        /* Sidebar visible cuando está abierto en móvil */
        .main-sidebar.mobile-open {
          transform: translateX(0) !important;
        }

        /* Backdrop visible en móvil cuando el menú está abierto */
        .sidebar-backdrop {
          display: block !important;
        }

        /* El botón de colapsar a iconos no aplica en modo drawer */
        .sidebar-collapse-button {
          display: none !important;
        }
      }

      @media (min-width: 769px) {
        /* En desktop, siempre visible */
        .main-sidebar {
          transform: translateX(0) !important;
        }
      }

      /* En iOS, 100vh incluye la barra de direcciones, así que el pie del menú
         quedaba por debajo del borde visible. dvh mide el alto realmente
         disponible; los navegadores que no lo soporten ignoran la regla y se
         quedan con el 100vh de arriba. */
      .main-sidebar {
        height: 100dvh !important;
      }
    `}</style>
  </>
  );
};

export default Sidebar;